import type { ComponentPropsWithRef, ReactNode } from 'react';
import { Radio } from '@src/components/Radio/Radio';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './Choice.css';

type NativeRadioProps = Omit<
  ComponentPropsWithRef<'input'>,
  'style' | 'type' | 'checked' | 'onChange' | 'children' | 'size'
>;

export interface ChoiceProps extends NativeRadioProps {
  /** Controlled. In a group, exactly one choice is selected. */
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** The name of the option. Omit it for a single line of `description`. */
  title?: string;
  description?: string;
  /** Optional leading artwork. Decorative — the title is the name. */
  icon?: ReactNode;
  /** Beside the title — a stitched “Recommended” tag, etc. */
  badge?: ReactNode;
  /** Trailing note instead of the radio — “Already chosen”, a lock, etc. */
  meta?: ReactNode;
  disabled?: boolean;
  /** Parent id. The radio hangs off it as `${testId}-radio`. */
  testId?: string;
}

export function Choice({
  checked,
  onCheckedChange,
  title,
  description,
  icon,
  badge,
  meta,
  disabled = false,
  className,
  name,
  value,
  testId,
  ...rest
}: ChoiceProps) {
  const classes = ['choice'];
  if (checked) {
    classes.push('choice--selected');
  }
  if (disabled) {
    classes.push('choice--disabled');
  }
  if (!description) {
    classes.push('choice--plain');
  }
  if (className) {
    classes.push(className);
  }

  const label = title || description;

  return (
    <label className={classes.join(' ')} {...testIdAttr(testId)}>
      {icon && (
        <span className="choice__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <span className="choice__copy">
        {title && (
          <span className="choice__heading">
            <Typography
              as="span"
              role="title/card"
              color="inherit"
              className="choice__title"
              {...testIdAttr(testIdFor(testId, 'title'))}
            >
              {title}
            </Typography>
            {badge ? <span className="choice__badge">{badge}</span> : null}
          </span>
        )}
        {description && !title && (
          <Typography
            as="span"
            role="title/card"
            color="inherit"
            className="choice__title"
            {...testIdAttr(testIdFor(testId, 'title'))}
          >
            {description}
          </Typography>
        )}
        {description && title && (
          <Typography
            as="span"
            role="body/sm"
            color="inherit"
            className="choice__description"
            {...testIdAttr(testIdFor(testId, 'description'))}
          >
            {description}
          </Typography>
        )}
      </span>
      {meta ? (
        <Typography
          as="span"
          role="meta/helper"
          color="inherit"
          className="choice__meta"
          {...testIdAttr(testIdFor(testId, 'meta'))}
        >
          {meta}
        </Typography>
      ) : (
        <Radio
          className="choice__radio"
          indicator
          checked={checked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
          name={name}
          value={value}
          testId={testIdFor(testId, 'radio')}
          aria-label={typeof label === 'string' ? label : undefined}
          {...rest}
        />
      )}
    </label>
  );
}
